import React, { useEffect, useState } from 'react'

import Alert from '../components/alert'

import './login.css';
import config from '../config.json'
import iconFingerprint from '../design/icons/fingerprint.svg'
import iconLogo from '../design/icons/logo-full-white.png'
import iconSlogan from '../design/icons/slogan.svg'
import {
  isWebAuthnSupported,
  loginWithPasskey,
} from './webauthn'
import { saveSession } from '../auth/session'
import { readClientServices, saveClientServices } from '../auth/clientServices'

const Login = (props) => {
 
  const [ alertOptions, setAlertOptions ] = useState(null)
  const [ client, setClient ] = useState(null)
  const [ notClient, setNotClient ] = useState(false)
  const [ loginServiceDown, setLoginServiceDown ] = useState(false)
  const [ biometricLoading, setBiometricLoading ] = useState(false)
  const [ savedUser ] = useState(() => localStorage.getItem('user') || '')
  const webAuthnAvailable = isWebAuthnSupported()

  const resetStoredClient = () => {
    localStorage.removeItem('client')
    config.client = null
    setClient(null)
    setNotClient(true)
    setLoginServiceDown(false)
  }

  useEffect( () => {

    const storedClient = props.client?.trim()

    if(!storedClient) {
     setNotClient(true)
    } else {
      getClient(storedClient)
    }
  }, [props.client])

  const closeAlert = () => {
    setAlertOptions(null)
  }

  const changeClient = () => {

      setAlertOptions( {
        title: 'Cambiar cliente',
        msg: `¿Desea cambiar el cliente ${client}?`,
        buttons: [
          { 
            label: 'Si', 
            onClick: () => {
              localStorage.clear()
              setClient(false)
              setNotClient(true)
            }
          },
          { 
            label: 'No',
            onClick: () => {
              setAlertOptions(null)
            }
          }
        ]
      })
  }

  const applyCachedServices = (clientId) => {
    const cached = readClientServices(clientId)

    if (!cached?.be) return false

    config.services = cached
    config.client = clientId
    setClient(clientId)
    setNotClient(false)
    setLoginServiceDown(true)

    return true
  }

  const getClient = async (clientId) => {

    let data

    try {
      const response = await fetch(`${config.logInUrl}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientId: clientId
        })
      })

      data = await response.json()
    } catch (error) {
      console.log(error)

      // Sin respuesta del servidor de login no se puede distinguir un cliente
      // invalido de una caida: no borrar el cliente guardado.
      if (!applyCachedServices(clientId)) {
        setClient(null)
        setNotClient(false)
        setLoginServiceDown(true)
      }

      return
    }

    if (data.status !== 'ok') {
      resetStoredClient()
      alert(data.msg)
      return
    }

    config.services = data.body.services
    config.client = clientId
    saveClientServices(clientId, data.body.services)
    setClient(clientId)
    setNotClient(false)
    setLoginServiceDown(false)
  }

  const completeLogin = (response, user) => {
    localStorage.setItem('user', user.toLowerCase())
    config.auth = response.auth
    config.name = response.name
    config.token = response.access
    config.apiUrl = config.services.be
    saveSession()
    props.setLogin(false)
  }

  const submit = async () => {

    if(notClient) {
      const client = document.getElementById("client-input").value;
      if(!client) {
        alert("Debe seleccionar un cliente");
        return;
      }
      await localStorage.setItem('client', client)
      await getClient(client)
      return;
    }

    if (!config.services?.be) {
      alert("No se pudo determinar el servidor del cliente. Reintente en unos minutos.");
      return;
    }

    const obj = {
      client: client,
      user: document.getElementById("user").value.toLowerCase(),
      pass: document.getElementById("pass").value
    };

    fetch(`${config.services.be}/login`, {
      method: "POST",
      body: JSON.stringify(obj),
      headers: { "Content-Type": "application/json" }
    })
      .then(res => res.json())
      .then(response => {

        if (response.status === "ok") {
          completeLogin(response, obj.user)
        } else {
          alert(response.msg);
        }
      })
      .catch(err =>{

        console.log(err)
        alert("No se pudo conectar al servidor. Asegurese que cuenta con conexion a Internet.");
      })

  }

  const submitWithPasskey = async () => {
    if (!client) {
      alert('Debe seleccionar un cliente')
      return
    }

    const user = document.getElementById('user')?.value?.trim()
    if (!user) {
      alert('Ingrese el usuario antes de usar biometria')
      return
    }

    setBiometricLoading(true)

    try {
      const response = await loginWithPasskey({
        client,
        user,
        apiUrl: config.services.be,
      })

      completeLogin(response, user)
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        alert('Operacion cancelada')
      } else {
        alert(err.message || 'No se pudo iniciar sesion con biometria')
      }
    } finally {
      setBiometricLoading(false)
    }
  }

  const enter = (e) => {

    const tecla = document.all ? e.keyCode : e.which;

    if (tecla === 13) {
        submit();
    }

  }

  const clientUnresolved = loginServiceDown && !client && !notClient

  const retryGetClient = () => {
    const storedClient = localStorage.getItem('client')?.trim()

    if (!storedClient) {
      setLoginServiceDown(false)
      setNotClient(true)
      return
    }

    getClient(storedClient)
  }

  return(
    <div className="login-page">

      <aside className="login-brand" aria-hidden="false">
        <div className="login-brand__pattern" aria-hidden="true" />
        <div className="login-brand__glow" aria-hidden="true" />
        <div className="login-brand__content">
          <img className="login-brand__logo" src={iconLogo} alt="Pymego" width="220" height="52" />
          <img className="login-brand__slogan" src={iconSlogan} alt="Progresamos Juntos" width="200" height="24" />
          <p className="login-brand__tagline">Gestión empresarial integral</p>
        </div>
      </aside>

      <main className="login-panel">
        <div className="login-card">
          <header className="login-card__header">
            <h1 className="login-card__title">Acceso al sistema</h1>
            <p className="login-card__subtitle">
              {clientUnresolved
                ? 'No se pudo contactar el servidor de acceso'
                : notClient
                  ? 'Ingrese el identificador de su empresa para continuar'
                  : 'Ingrese sus credenciales para continuar'}
            </p>
          </header>

          {loginServiceDown && (
            <p className="login-notice">
              {clientUnresolved
                ? 'El servidor de acceso no responde. Sus datos siguen guardados en este dispositivo: reintente en unos minutos.'
                : 'El servidor de acceso no responde. Continuamos con los datos guardados en este dispositivo.'}
            </p>
          )}

          {client && (
            <div
              className="login-client-badge"
              id="cliente"
              onClick={changeClient}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') changeClient() }}
              title="Cambiar cliente"
            >
              <span className="login-client-badge__label">Cliente</span>
              <span className="login-client-badge__value">{client}</span>
              <span className="login-client-badge__hint">Cambiar</span>
            </div>
          )}

          <div className="login-fields">
            {client && (
              <>
                <label className="login-field" htmlFor="user">
                  <span className="login-field__label">Usuario</span>
                  <input
                    className="login-field__input"
                    type="text"
                    id="user"
                    placeholder="nombre.de.usuario"
                    defaultValue={savedUser}
                    onKeyPress={enter}
                    required
                    autoComplete="username"
                  />
                </label>
                <label className="login-field" htmlFor="pass">
                  <span className="login-field__label">Contraseña</span>
                  <input
                    className="login-field__input"
                    type="password"
                    id="pass"
                    placeholder="••••••••"
                    onKeyPress={enter}
                    required
                    autoComplete="current-password"
                  />
                </label>
              </>
            )}
            {notClient && (
              <label className="login-field" htmlFor="client-input">
                <span className="login-field__label">Cliente</span>
                <input
                  className="login-field__input"
                  type="text"
                  id="client-input"
                  placeholder="Identificador de empresa"
                  onKeyPress={enter}
                  required
                  autoComplete="organization"
                />
              </label>
            )}
          </div>

          <div className="login-actions">
            {clientUnresolved ? (
              <button
                type="button"
                className="login-button"
                onClick={retryGetClient}
              >
                Reintentar
              </button>
            ) : (
              <button
                id="btn-submit"
                type="button"
                className="login-button"
                onClick={submit}
                disabled={biometricLoading}
              >
                Entrar
              </button>
            )}

            {client && webAuthnAvailable && (
              <button
                type="button"
                className={`login-biometric-button${biometricLoading ? ' login-biometric-button--loading' : ''}`}
                onClick={submitWithPasskey}
                disabled={biometricLoading}
                aria-label="Entrar con biometria"
              >
                <img className="login-biometric-icon" src={iconFingerprint} alt="" />
                Entrar con biometría
              </button>
            )}
          </div>

          <footer className="login-card__footer">
            <p className="login-card__footer-text">Pymego · Sistema de gestión empresarial</p>
          </footer>
        </div>
      </main>

      {alertOptions && (
        <Alert options={alertOptions} closeAlert={closeAlert} />
      )}
    </div>
  )
}

export default Login
